import { create } from 'zustand';

import { send } from '@/api/send';
import type { TransactionEntity } from '@/api/types';

export type TransactionFilter = {
  search: string;
  startDate: string | null;
  endDate: string | null;
};

type TransactionsState = {
  accountIds: string[] | null;
  transactions: TransactionEntity[];
  loading: boolean;
  error: string | null;
  selected: string[];
  filter: TransactionFilter;
  load: (accountIds: string[] | null) => Promise<void>;
  setFilter: (patch: Partial<TransactionFilter>) => void;
  toggleSelected: (id: string) => void;
  setSelected: (ids: string[]) => void;
  update: (
    id: string,
    field: keyof TransactionEntity,
    value: unknown,
  ) => Promise<void>;
  create: (
    partial: Partial<TransactionEntity> & {
      account: string;
      date: string;
      amount: number;
    },
  ) => Promise<void>;
  remove: (id: string) => Promise<void>;
  refreshBalances: () => Promise<void>;
};

export const useTransactionsStore = create<TransactionsState>((set, get) => ({
  accountIds: null,
  transactions: [],
  loading: false,
  error: null,
  selected: [],
  filter: { search: '', startDate: null, endDate: null },

  async load(accountIds) {
    set({ loading: true, accountIds, error: null });
    try {
      const transactions = await send('transactions-get', {
        accountIds,
        startDate: get().filter.startDate,
        endDate: get().filter.endDate,
      });
      set({ transactions, loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '交易加载失败',
      });
    }
  },

  setFilter(patch) {
    set(state => ({ filter: { ...state.filter, ...patch } }));
    void get().load(get().accountIds);
  },

  toggleSelected(id) {
    set(state => ({
      selected: state.selected.includes(id)
        ? state.selected.filter(item => item !== id)
        : [...state.selected, id],
    }));
  },

  setSelected(ids) {
    set({ selected: ids });
  },

  async update(id, field, value) {
    const changes = [{ id, field: field as string, value }];
    set(state => ({
      transactions: state.transactions.map(txn =>
        txn.id === id ? { ...txn, [field]: value } : txn,
      ),
    }));
    const result = await send('transactions-batch-update', { changes });
    // The mock resolves names to ids (and may create payees/categories), so
    // the server row wins over the optimistic patch.
    if (result.updated.length) {
      set(state => ({
        transactions: state.transactions.map(txn => {
          const fresh = result.updated.find(item => item.id === txn.id);
          return fresh ?? txn;
        }),
      }));
    }
    if (field === 'amount' || field === 'account' || field === 'cleared') {
      await get().refreshBalances();
    }
  },

  async create(partial) {
    await send('transaction-create', partial);
    await get().load(get().accountIds);
    await get().refreshBalances();
  },

  async remove(id) {
    set(state => ({
      transactions: state.transactions.filter(txn => txn.id !== id),
      selected: state.selected.filter(item => item !== id),
    }));
    await send('transaction-delete', { id });
    await get().refreshBalances();
  },

  refreshBalances: async () => {
    // Balances live in the app store; pull the fresh numbers after a write.
    const { useAppStore } = await import('./app');
    await useAppStore.getState().refreshAccounts();
  },
}));
